import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import api from '@/lib/api';
import { MapPin, Search, ExternalLink, Loader2, Info, Flag, X } from 'lucide-react';
import SEO from '@/components/SEO';
import { CommunityGrowthNote } from '@/components/CommunityGrowthNote';

const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLSdihOAtFliZ4xIwrm9U5VR81Er8OIW9SduoYrKXR0qBhho0HQ/viewform?usp=publish-editor";
const DISTANCE_OPTIONS = [
  { value: '10', label: '10 mi' },
  { value: '25', label: '25 mi' },
  { value: '50', label: '50 mi' },
  { value: '100', label: '100 mi' },
  { value: 'any', label: 'Any' },
];

export default function PlayLocationsPage() {
  const { user } = useAuth();

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [zipCode, setZipCode] = useState('');
  const [maxDistance, setMaxDistance] = useState('25');
  const [showForm, setShowForm] = useState(false);
  const [reportingId, setReportingId] = useState(null);
  const [reportData, setReportData] = useState({ email: '', issue: '' });
  const [reportSending, setReportSending] = useState(false);

  // Default zip to user's profile zip code and auto-search
  const didAutoSearch = useRef(false);
  useEffect(() => {
    if (user?.zip_code && !zipCode && !didAutoSearch.current) {
      setZipCode(user.zip_code);
    }
  }, [user, zipCode]);

  const handleReport = async (loc) => {
    if (!reportData.issue.trim()) { toast.error('Please describe the issue'); return; }
    setReportSending(true);
    try {
      await api.post('/play-locations/report', {
        location_name: loc.name,
        location_address: loc.address,
        location_zip: loc.zip_code,
        reporter_email: reportData.email,
        issue: reportData.issue,
      });
      toast.success('Report submitted. Thank you!');
      setReportingId(null);
      setReportData({ email: '', issue: '' });
    } catch { toast.error('Failed to submit report'); }
    finally { setReportSending(false); }
  };

  const handleSearch = useCallback(async () => {
    setLoading(true);
    setSearched(true);
    try {
      const params = new URLSearchParams();
      if (zipCode) params.set('zip_code', zipCode);
      if (maxDistance && maxDistance !== 'any') params.set('max_distance', maxDistance);
      const { data } = await api.get(`/play-locations?${params.toString()}`);
      setLocations(data.locations || []);
    } catch {
      setLocations([]);
    } finally { setLoading(false); }
  }, [zipCode, maxDistance]);

  // Auto-search once when the user's profile zip arrives. Open to free
  // and anon users alike now that the paywall is lifted, so we drop the
  // isPaid gate. Anon viewers don't have a profile zip → no auto-search.
  useEffect(() => {
    if (zipCode && zipCode.length === 5 && !didAutoSearch.current) {
      didAutoSearch.current = true;
      handleSearch();
    }
  }, [zipCode, handleSearch]);

  return (
    <div data-testid="play-locations-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SEO
        title="Public Mahjong Play Locations Near You"
        description="Discover public places to play American Mahjong — coffee shops, libraries, community centers, clubs, and regular meetups across the United States."
        path="/play-locations"
      />
      <div className="text-center mb-10">
        <span className="text-sm font-bold uppercase tracking-[0.2em] text-[#56c6df]">Play Locations</span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Public Places to Play Mahjong
        </h1>
        <p className="text-slate-500 max-w-xl mx-auto">Find coffee shops, libraries, community centers, and other venues where you can play Mahjong.</p>
      </div>

      {/* Suggest a Location — always visible at top */}
      <div className="mb-8 p-6 rounded-2xl border-2 border-dashed border-[#df5681]/30 bg-[#df5681]/5 text-center" data-testid="suggest-section">
        <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Know a Great Place to Play?</h2>
        <p className="text-slate-500 text-sm mb-4">Help our community grow by suggesting a public venue where Mahjong players are welcome.</p>
        <Button onClick={() => setShowForm(!showForm)} className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] px-6 gap-2" data-testid="suggest-location-btn">
          <ExternalLink className="w-4 h-4" />{showForm ? 'Hide Form' : 'Suggest a Location'}
        </Button>
        {showForm && (
          <div className="mt-5 max-w-3xl mx-auto rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid="location-form-embed">
            <iframe
              src={GOOGLE_FORM_URL}
              width="100%"
              height="800"
              frameBorder="0"
              title="Suggest a Play Location"
              className="w-full"
            >
              Loading form...
            </iframe>
          </div>
        )}
      </div>

      {/* Search section — open to everyone. "Search nearby play locations"
          is listed as an Open Play (free) feature on the pricing page,
          and we want Google + anon visitors to land on nearest-first
          results when they click in from search. Anon users see a CTA
          underneath the controls instead of a hard paywall. */}
      <>
          <CommunityGrowthNote surface="play-locations" />
          {!user && (
            <div className="mb-6 p-4 rounded-2xl bg-[#56c6df]/5 border border-[#56c6df]/20" data-testid="locations-anon-cta">
              <p className="text-sm font-semibold text-slate-700 mb-2">Sign up free to get more from play locations:</p>
              <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside ml-1">
                <li>Save your home zip so distance auto-calculates each visit</li>
                <li>Get alerts when new play locations are added near you</li>
                <li>Connect with other players who play at the same venues</li>
              </ul>
              <p className="text-xs text-slate-500 mt-2">Anyone can search venues and suggest new ones below — no account needed for that.</p>
              <Link to="/register" className="inline-block mt-3 text-sm font-semibold bg-clip-text text-transparent hover:underline" style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }}>
                Sign up free &rarr;
              </Link>
            </div>
          )}
          {/* Search Controls */}
          <Card className="border-slate-100 mb-8">
            <CardContent className="p-5">
              <div className="flex flex-col sm:flex-row gap-4 items-end">
                <div className="flex-1">
                  <Label className="text-sm font-medium mb-1.5 block">Your Zip Code</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      value={zipCode}
                      onChange={e => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                      placeholder="Enter your zip code"
                      className="pl-10 rounded-xl"
                      maxLength={5}
                      data-testid="location-zip-search"
                      onKeyDown={e => e.key === 'Enter' && zipCode.length === 5 && handleSearch()}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium mb-1.5 block">Distance</Label>
                  <div className="flex gap-1.5">
                    {DISTANCE_OPTIONS.map(o => (
                      <button
                        key={o.value}
                        onClick={() => setMaxDistance(o.value)}
                        className={`px-3 py-2 rounded-full text-xs border transition-all ${
                          maxDistance === o.value ? 'bg-[#56c6df] text-white border-[#56c6df]' : 'border-slate-200 hover:border-slate-300'
                        }`}
                        data-testid={`distance-${o.value}`}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={handleSearch}
                  disabled={loading}
                  className="rounded-full bg-[#56c6df] text-white hover:bg-[#3eb0c9] px-6 gap-2"
                  data-testid="search-locations-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Search
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#56c6df]" /></div>
          ) : searched && locations.length === 0 ? (
            <div className="text-center py-16 px-6" data-testid="locations-empty-state">
              <MapPin className="w-12 h-12 mx-auto mb-4 text-[#56c6df] opacity-70" />
              <p className="text-lg font-semibold text-slate-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {zipCode ? `No Mahjong-friendly venues near ${zipCode} yet` : 'No locations match your search yet'}
              </p>
              <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto leading-relaxed">
                Mahjong needs a table — and the best ones are the spots <span className="font-medium text-slate-700">someone first thought to suggest.</span> Know a coffee shop, library, club, or community center where players gather? Add it to the map.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mt-5">
                <Link to="/play-locations/suggest">
                  <Button className="rounded-full text-white font-semibold border-0 hover:brightness-110 transition-all" style={{ backgroundImage: 'linear-gradient(135deg, #56dfb4, #56c6df, #b456df)' }} data-testid="locations-empty-suggest-cta">
                    Suggest a location
                  </Button>
                </Link>
              </div>
            </div>
          ) : !searched ? (
            <div className="text-center py-16 text-slate-400">
              <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Enter your zip code to get started</p>
              <p className="text-sm mt-1">We'll find public Mahjong-friendly venues near you.</p>
            </div>
          ) : (
            <div className="space-y-4 mb-10">
              <p className="text-sm text-slate-500">{locations.length} location{locations.length !== 1 ? 's' : ''} found{zipCode ? ` near ${zipCode}` : ''}</p>
              {locations.map(loc => (
                <Card key={loc.id} className="border-slate-100 hover:shadow-md transition-all duration-300" data-testid={`location-card-${loc.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-[#56c6df]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <MapPin className="w-5 h-5 text-[#56c6df]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-semibold text-base" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="location-name">{loc.name || 'Unnamed Location'}</h3>
                          {loc.distance !== undefined && loc.distance !== null && (
                            <span className="text-xs font-medium text-[#56c6df] bg-[#56c6df]/10 px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0" data-testid="location-distance">
                              ~{loc.distance} mi
                            </span>
                          )}
                        </div>
                        {loc.space_type && (
                          <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full bg-[#b456df]/10 text-[#b456df] text-xs font-medium" data-testid="location-type">{loc.space_type}</span>
                        )}
                        {loc.address && (
                          <p className="text-sm text-slate-600 mt-2" data-testid="location-address">
                            <span className="font-medium text-slate-700">Address:</span> {loc.address}
                          </p>
                        )}
                        {loc.zip_code && (
                          <p className="text-sm text-slate-500 mt-0.5" data-testid="location-zip">
                            <span className="font-medium text-slate-700">Zip Code:</span> {loc.zip_code}
                          </p>
                        )}
                        {loc.notes && (
                          <div className="flex items-start gap-1.5 mt-2 text-sm text-slate-500" data-testid="location-notes">
                            <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                            <span>{loc.notes}</span>
                          </div>
                        )}
                        {/* Report Issue */}
                        {reportingId === loc.id ? (
                          <div className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-200 space-y-2" data-testid={`report-form-${loc.id}`}>
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-medium text-slate-700">Report an issue with this location</p>
                              <button onClick={() => setReportingId(null)} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
                            </div>
                            <Input value={reportData.email} onChange={e => setReportData(p => ({...p, email: e.target.value}))} placeholder="Your email (optional)" className="rounded-lg text-xs h-8" data-testid="report-email" />
                            <Textarea value={reportData.issue} onChange={e => setReportData(p => ({...p, issue: e.target.value}))} placeholder="Describe the issue (e.g. location closed, incorrect info, no longer allows Mahjong...)" rows={2} className="rounded-lg text-xs" data-testid="report-issue" />
                            <Button onClick={() => handleReport(loc)} disabled={reportSending} size="sm" className="rounded-full bg-[#df5681] text-white hover:bg-[#c44570] text-xs gap-1" data-testid="report-submit-btn">
                              {reportSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />}Submit Report
                            </Button>
                          </div>
                        ) : (
                          <button onClick={() => { setReportingId(loc.id); setReportData({ email: '', issue: '' }); }} className="mt-2 flex items-center gap-1 text-xs text-slate-400 hover:text-[#df5681] transition-colors" data-testid={`report-btn-${loc.id}`}>
                            <Flag className="w-3 h-3" />Report an issue
                          </button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
      </>
    </div>
  );
}
